import { useState, useEffect, useRef, useCallback } from "react";
import {
  FiMessageSquare,
  FiSend,
  FiX,
  FiMinimize2,
  FiMaximize2,
  FiUser,
} from "react-icons/fi";
import { API_ENDPOINTS, WS_ENDPOINTS } from "@/constants/constants";
import { io } from "socket.io-client";
import type { Socket } from "socket.io-client";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profileImage?: string | null;
  role?: string;
}

interface Message {
  id: string;
  content: string;
  senderId: string;
  senderName: string;
  receiverId: string;
  timestamp: string;
}

interface ApiMessage {
  id: string | number;
  content: string;
  isRead?: boolean;
  attachmentUrl?: string | null;
  sender_id?: string | number;
  senderId?: string | number;
  senderName?: string;
  recipient_id?: string | number;
  receiverId?: string | number;
  createdAt?: string;
  timestamp?: string;
  sender?: {
    id: string | number;
    firstName: string;
    lastName: string;
    email?: string;
    profileImage?: string | null;
  };
  recipient?: {
    id: string | number;
    firstName: string;
    lastName: string;
  };
}

const FloatingChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [connected, setConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);

  const socketRef = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  // Fetch users from API
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(
        API_ENDPOINTS.AUTH.GET_USERS,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data && Array.isArray(data)) {
        // Filter out current user and process data
        const filteredUsers = data
          .filter((user: User) => user.id !== currentUser.id)
          .map((user: User) => ({
            ...user,
            profileImage: user.profileImage
              ? user.profileImage.startsWith("http")
                ? user.profileImage
                : `${API_ENDPOINTS.AUTH.LOGIN.split("/api")[0]}/${
                    user.profileImage
                  }`
              : null,
          }));

        setUsers(filteredUsers);
      }

      setError(null);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setError("Failed to load users");
      // Mock data for development
      setUsers([
        {
          id: "1",
          firstName: "John",
          lastName: "Doe",
          email: "john@example.com",
          role: "STUDENT",
          profileImage: null,
        },
        {
          id: "2",
          firstName: "Jane",
          lastName: "Smith",
          email: "jane@example.com",
          role: "TEACHER",
          profileImage: null,
        },
        {
          id: "3",
          firstName: "Alice",
          lastName: "Williams",
          email: "alice@example.com",
          role: "STUDENT",
          profileImage: null,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [currentUser.id]);

  // Helper to get user name from ID
  const getUserName = useCallback(
    (userId: string | number): string => {
      if (userId.toString() === currentUser.id?.toString()) {
        return "Me";
      }

      const user = users.find((u) => u.id.toString() === userId.toString());
      return user ? `${user.firstName} ${user.lastName}` : "Unknown User";
    },
    [currentUser.id, users]
  );

  // Handler for connection
  const handleSocketConnect = useCallback(() => {
    console.log("Connected to WebSocket server");
    setConnected(true);
  }, []);

  // Handler for user status changes
  const handleUserStatusChanged = useCallback(
    (data: { userId: number; isOnline: boolean; timestamp: Date }) => {
      console.log("Statut utilisateur changé:", data);
      const userIdStr = data.userId.toString();
      setOnlineUsers((prev) => {
        const newSet = new Set(prev);
        if (data.isOnline) {
          newSet.add(userIdStr);
        } else {
          newSet.delete(userIdStr);
        }
        return newSet;
      });
    },
    []
  );

  // Helper to extract sender ID and name from message
  const extractSenderInfo = useCallback(
    (message: ApiMessage) => {
      const senderId =
        message.sender?.id?.toString() ||
        message.sender_id?.toString() ||
        message.senderId?.toString() ||
        "";
      const senderName =
        message.senderName ||
        (message.sender
          ? `${message.sender.firstName} ${message.sender.lastName}`
          : "") ||
        getUserName(senderId);
      return { senderId, senderName };
    },
    [getUserName]
  );

  // Helper to extract receiver ID from message
  const extractReceiverId = useCallback((message: ApiMessage) => {
    return (
      message.recipient?.id?.toString() ||
      message.recipient_id?.toString() ||
      message.receiverId?.toString() ||
      ""
    );
  }, []);

  // Helper to check if message belongs to current conversation
  const belongsToCurrentConversation = useCallback(
    (message: Message): boolean => {
      if (!selectedUser) return false;
      return (
        message.senderId === selectedUser.id || message.receiverId === selectedUser.id
      );
    },
    [selectedUser]
  );

  // Handler for new messages
  const handleNewMessage = useCallback(
    (apiMessage: ApiMessage) => {
      console.log("Message reçu:", apiMessage);
      try {
        const { senderId, senderName } = extractSenderInfo(apiMessage);
        const receiverId = extractReceiverId(apiMessage);

        const newMsg: Message = {
          id: apiMessage.id.toString(),
          content: apiMessage.content,
          senderId,
          senderName,
          receiverId,
          timestamp:
            apiMessage.createdAt ||
            apiMessage.timestamp ||
            new Date().toISOString(),
        };

        if (!belongsToCurrentConversation(newMsg)) return;

        setMessages((prev) => [...prev, newMsg]);
        scrollToBottom();

        if (socketRef.current && newMsg.senderId === selectedUser?.id) {
          socketRef.current.emit("read_messages", {
            otherUserId: Number(selectedUser.id),
          });
        }
      } catch (error) {
        console.error("Erreur lors du traitement du message:", error, apiMessage);
      }
    },
    [extractSenderInfo, extractReceiverId, belongsToCurrentConversation, selectedUser]
  );

  // Handler for typing notifications
  const handleUserTyping = useCallback(
    (data: { userId: number; channelId?: number; isTyping: boolean }) => {
      if (!selectedUser?.id || data.userId.toString() !== selectedUser?.id) return;

      const userName = getUserName(data.userId);
      setTypingUsers((prev) => {
        if (data.isTyping) {
          return prev.includes(userName) ? prev : [...prev, userName];
        }
        return prev.filter((name) => name !== userName);
      });
    },
    [selectedUser, getUserName]
  );

  // Setup WebSocket connection
  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      setError("You are not logged in");
      return;
    }

    const socket = io(WS_ENDPOINTS.CHAT.MAIN, { auth: { token: accessToken } });
    socketRef.current = socket;

    socket.on("connect", handleSocketConnect);
    socket.on("disconnect", () => {
      console.log("Disconnected from WebSocket server");
      setConnected(false);
      setOnlineUsers(new Set());
    });
    socket.on("connect_error", (err: Error) => {
      console.error("WebSocket connection error:", err);
      setError("Failed to connect to chat server");
      setConnected(false);
    });
    socket.on("user_status_changed", handleUserStatusChanged);
    socket.on("online_users", (data: { userIds: number[] }) => {
      const onlineUserIds = new Set(data.userIds.map((id) => id.toString()));
      setOnlineUsers(onlineUserIds);
    });
    socket.on("new_message", handleNewMessage);
    socket.on("messages_read", (data: { byUserId: number }) => {
      console.log("Messages read by user:", data.byUserId);
    });
    socket.on("user_typing", handleUserTyping);

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  // Charger les utilisateurs quand le chat est ouvert
  useEffect(() => {
    if (isOpen && connected) {
      fetchUsers();
    }
  }, [isOpen, connected]);

  // Fetch messages when a user is selected
  useEffect(() => {
    if (selectedUser) {
      fetchMessages(selectedUser.id);
    } else {
      setMessages([]);
    }
  }, [selectedUser?.id]);

  // Fetch messages for the selected user
  const formatApiMessage = useCallback(
    (msg: ApiMessage): Message => {
      const { senderId, senderName } = extractSenderInfo(msg);
      const receiverId = extractReceiverId(msg);
      const timestamp =
        msg.createdAt || msg.timestamp || new Date().toISOString();

      return {
        id: msg.id.toString(),
        content: msg.content,
        senderId,
        senderName,
        receiverId,
        timestamp,
      };
    },
    [extractSenderInfo, extractReceiverId]
  );

  const fetchMessages = async (userId: string) => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(
        API_ENDPOINTS.CHAT.GET_PRIVATE_MESSAGES(userId),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Messages récupérés:", data);

      if (!Array.isArray(data)) {
        setMessages([]);
        return;
      }

      const formattedMessages = data.map(msg => formatApiMessage(msg));
      setMessages(formattedMessages);
      scrollToBottom();
    } catch (err) {
      console.error("Failed to fetch messages:", err);
      setError("Failed to load messages");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  // Send a message
  const sendMessage = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!newMessage.trim() || !selectedUser || !socketRef.current) {
      return;
    }

    // Create message payload
    const messagePayload = {
      content: newMessage,
      recipientId: Number(selectedUser.id),
    };

    // Send via WebSocket
    socketRef.current.emit("send_message", messagePayload);

    // Clear input
    setNewMessage("");

    // Notify we're not typing anymore
    sendTypingStatus(false);
  };

  // Format time for display
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Toggle chat open/closed
  const toggleChat = () => {
    const newOpenState = !isOpen;
    setIsOpen(newOpenState);
    setIsMinimized(false);

    // Rafraîchir les statuts quand on ouvre le chat
    if (newOpenState && connected) {
      refreshOnlineStatus();
    }
  };

  // Toggle chat minimized/maximized
  const toggleMinimize = () => {
    setIsMinimized((prev) => !prev);
  };

  // Select a user to chat with
  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setIsMinimized(false);

    // Rafraîchir les statuts après avoir sélectionné un utilisateur
    refreshOnlineStatus();
  };

  // Pour la fonction handleInputChange
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);

    // Notify typing status
    if (e.target.value.trim() && selectedUser?.id) {
      sendTypingStatus(true);
    }

    // Reset typing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set timeout to stop typing notification
    typingTimeoutRef.current = setTimeout(() => {
      if (e.target.value.trim()) {
        sendTypingStatus(false);
      }
    }, 2000);
  };

  // Fonction pour envoyer le statut de frappe
  const sendTypingStatus = (isTyping: boolean) => {
    if (!socketRef.current || !selectedUser) return;

    socketRef.current.emit("typing", {
      recipientId: Number(selectedUser.id),
      isTyping,
    });
  };

  const clearError = () => {
    setError(null);
  };

  // Fonction pour demander la liste des utilisateurs en ligne
  const refreshOnlineStatus = () => {
    if (socketRef.current && connected) {
      console.log("Demande de rafraîchissement des statuts utilisateurs");
      socketRef.current.emit("get_online_users", {});
    }
  };

  // Helper to render message bubble
  const renderMessageBubble = (message: Message) => {
    const isCurrentUser = message.senderId === currentUser.id?.toString();
    return (
      <div
        key={message.id}
        className={`flex ${isCurrentUser ? "justify-end" : "justify-start"}`}
      >
        {!isCurrentUser && (
          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-2">
            <FiUser size={16} className="text-gray-600" />
          </div>
        )}
        <div
          className={`max-w-[85%] rounded-lg px-3 py-2 
            ${
              isCurrentUser
                ? "bg-green-600 text-white"
                : "bg-white border border-gray-200"
            }`}
        >
          <p className="text-sm">{message.content}</p>
          <span
            className={`text-xs block mt-1 
              ${isCurrentUser ? "text-green-100" : "text-gray-500"}`}
          >
            {isCurrentUser ? "Moi" : message.senderName} •{" "}
            {formatTime(message.timestamp)}
          </span>
        </div>
      </div>
    );
  };

  // Helper to render typing indicators
  const renderTypingIndicator = () => {
    if (typingUsers.length === 0) return null;
    return (
      <div className="flex items-center text-xs text-gray-500 italic">
        <div className="flex-shrink-0 w-8 h-3 flex justify-start space-x-1 mr-2">
          <span
            className="block w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
            style={{ animationDelay: "0ms" }}
          ></span>
          <span
            className="block w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
            style={{ animationDelay: "150ms" }}
          ></span>
          <span
            className="block w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
            style={{ animationDelay: "300ms" }}
          ></span>
        </div>
        {typingUsers.join(", ")} {typingUsers.length === 1 ? "is" : "are"}{" "}
        typing...
      </div>
    );
  };

  // Helper to render status indicator
  const renderStatusIndicator = (userId: string) => {
    const isOnline = onlineUsers.has(userId);
    return (
      <div
        className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
          isOnline ? "bg-green-500" : "bg-red-500"
        }`}
      />
    );
  };

  // Helper to get online status text
  const getStatusText = (userId: string): string => {
    return onlineUsers.has(userId) ? "en ligne" : "hors ligne";
  };

  // Handle user list item key press
  const handleUserListItemKeyDown = (e: React.KeyboardEvent, user: User) => {
    if (e.key === "Enter" || e.key === " ") {
      handleUserSelect(user);
    }
  };

  // Handle user profile image error
  const handleProfileImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    target.onerror = null;
    target.src = "/images/avatar-placeholder.png";
  };

  // Render user profile image
  const renderUserProfileImage = (user: User) => {
    if (user.profileImage) {
      return (
        <img
          src={user.profileImage}
          alt={`${user.firstName} ${user.lastName}`}
          className="w-8 h-8 rounded-full object-cover"
          onError={handleProfileImageError}
        />
      );
    }
    return <FiUser size={16} className="text-gray-600" />;
  };

  // Helper to render user list item
  const renderUserListItem = (user: User) => {
    const isSelected = selectedUser?.id === user.id;
    const statusText = getStatusText(user.id);
    const statusColor = onlineUsers.has(user.id) ? "text-green-500" : "text-red-500";

    return (
      <button
        key={user.id}
        onClick={() => handleUserSelect(user)}
        onKeyDown={(e) => handleUserListItemKeyDown(e, user)}
        className={`p-2 rounded-md flex items-center cursor-pointer hover:bg-gray-100 w-full justify-start ${
          isSelected ? "bg-blue-50" : ""
        }`}
      >
        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center relative mr-2">
          {renderUserProfileImage(user)}
          {renderStatusIndicator(user.id)}
        </div>
        <div className="flex flex-col">
          <div className="text-sm font-medium">
            {user.firstName} {user.lastName}
          </div>
          <div className={`text-xs ${statusColor}`}>{statusText}</div>
        </div>
      </button>
    );
  };

  // Render selected user profile image
  const renderSelectedUserProfileImage = () => {
    if (!selectedUser) return null;
    if (selectedUser.profileImage) {
      return (
        <img
          src={selectedUser.profileImage}
          alt={`${selectedUser.firstName} ${selectedUser.lastName}`}
          className="w-8 h-8 rounded-full object-cover"
          onError={handleProfileImageError}
        />
      );
    }
    return <FiUser size={16} className="text-gray-600" />;
  };

  // Helper to render selected user header info
  const renderSelectedUserHeader = () => {
    if (!selectedUser) return null;
    const isOnline = onlineUsers.has(selectedUser.id);
    const statusColor = isOnline ? "text-green-500" : "text-red-500";

    return (
      <>
        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center relative mr-2">
          {renderSelectedUserProfileImage()}
          {renderStatusIndicator(selectedUser.id)}
        </div>
        <div>
          <div className="font-medium flex items-center">
            {selectedUser.firstName} {selectedUser.lastName}
            <span className={`ml-2 text-xs ${statusColor}`}>
              {getStatusText(selectedUser.id)}
            </span>
          </div>
          {selectedUser.email && (
            <div className="text-xs text-gray-500">{selectedUser.email}</div>
          )}
        </div>
      </>
    );
  };

  // Déclencher un rafraîchissement périodique des statuts
  useEffect(() => {
    // Configurer un rafraîchissement périodique des statuts
    const statusInterval = setInterval(() => {
      if (isOpen && connected) {
        refreshOnlineStatus();
      }
    }, 10000); // Rafraîchir toutes les 10 secondes

    return () => {
      clearInterval(statusInterval);
    };
  }, [isOpen, connected]);

  // Handle minimize button key press
  const handleMinimizeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      toggleMinimize();
    }
  };

  // Vérifie si la touche est Entrée ou Espace
  const isEnterOrSpace = (e: React.KeyboardEvent) =>
    e.key === "Enter" || e.key === " ";

  // Handle close button key press
  const handleCloseKeyDown = (e: React.KeyboardEvent) => {
    if (isEnterOrSpace(e)) {
      toggleChat();
    }
  };

  // Render chat header buttons
  const renderChatHeaderButtons = () => (
    <div className="flex space-x-2">
      <button
        onClick={toggleMinimize}
        onKeyDown={handleMinimizeKeyDown}
        className="hover:bg-blue-700 rounded p-1"
      >
        {isMinimized ? (
          <FiMaximize2 size={18} />
        ) : (
          <FiMinimize2 size={18} />
        )}
      </button>
      <button
        onClick={toggleChat}
        onKeyDown={handleCloseKeyDown}
        className="hover:bg-blue-700 rounded p-1"
      >
        <FiX size={18} />
      </button>
    </div>
  );

  // Render chat header
  const renderChatHeader = () => (
    <div className="bg-blue-600 text-white p-3 rounded-t-lg flex justify-between items-center">
      <h3 className="font-medium flex items-center">
        {selectedUser
          ? `Chat with ${selectedUser.firstName} ${selectedUser.lastName}`
          : "Admin Chat"}
        <span
          className={`ml-2 text-xs flex items-center ${
            connected ? "text-green-300" : "text-red-300"
          }`}
        >
          <div
            className={`w-2 h-2 rounded-full mr-1 ${
              connected ? "bg-green-300" : "bg-red-300"
            }`}
          ></div>
          {connected ? "connected" : "disconnected"}
        </span>
      </h3>
      {renderChatHeaderButtons()}
    </div>
  );

  // Handle error close button key press
  const handleErrorCloseKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      clearError();
    }
  };

  // Render message input form
  const renderMessageInputForm = () => (
    <form
      onSubmit={sendMessage}
      className="p-2 border-t border-gray-200 flex"
    >
      <input
        type="text"
        value={newMessage}
        onChange={handleInputChange}
        placeholder="Type a message..."
        className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <button
        type="submit"
        disabled={!connected}
        className={`${
          connected
            ? "bg-green-600 hover:bg-green-700"
            : "bg-red-500"
        } text-white px-3 py-2 rounded-r-md disabled:opacity-50`}
      >
        <FiSend size={16} />
      </button>
    </form>
  );

  // Render error message
  const renderErrorMessage = () => {
    if (!error) return null;
    return (
      <div className="p-2 bg-red-50 border-t border-red-200">
        <div className="flex items-center justify-between">
          <p className="text-xs text-red-600">{error}</p>
          <button
            onClick={clearError}
            onKeyDown={handleErrorCloseKeyDown}
            className="text-xs text-red-500 hover:text-red-700"
          >
            Fermer
          </button>
        </div>
      </div>
    );
  };

  // Handle toggle button key press (reuses handleCloseKeyDown logic)
  const handleToggleChatKeyDown = handleCloseKeyDown;


  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Chat toggle button */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          onKeyDown={handleToggleChatKeyDown}
          className={`${
            connected
              ? "bg-green-600 hover:bg-green-700"
              : "bg-red-600 hover:bg-red-700"
          } text-white rounded-full p-3 shadow-lg flex items-center justify-center relative`}
        >
          <FiMessageSquare size={24} />
          <div className="absolute top-0 right-0">
            {onlineUsers.size > 0 && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] font-bold text-green-600">
                {onlineUsers.size}
              </span>
            )}
          </div>
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div
          ref={chatContainerRef}
          className="bg-white rounded-lg shadow-xl flex flex-col"
          style={{
            width: "360px",
            height: isMinimized ? "auto" : "500px",
            transition: "height 0.3s ease-in-out",
          }}
        >
          {/* Chat header */}
          {renderChatHeader()}

          {!isMinimized && (
            <>
              <div className="flex-1 flex overflow-hidden">
                {/* Users list */}
                <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
                  <div className="p-2">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">
                      Utilisateurs
                    </h4>
                    {loading && users.length === 0 ? (
                      <div className="text-center py-4 text-sm text-gray-500">
                        Chargement...
                      </div>
                    ) : users.length === 0 ? (
                      <div className="text-center py-4 text-sm text-gray-500">
                        Aucun utilisateur
                      </div>
                    ) : (
                      <ul>
                        {users.map(user => renderUserListItem(user))}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Chat content */}
                <div className="w-2/3 flex flex-col">
                  {selectedUser ? (
                    <>
                      {/* Header with user info */}
                      <div className="border-b border-gray-200 p-3 flex items-center">
                        {renderSelectedUserHeader()}
                      </div>

                      {/* Messages */}
                      <div className="flex-1 p-3 overflow-y-auto bg-gray-50">
                        {loading ? (
                          <div className="text-center py-4 text-sm text-gray-500">
                            Loading messages...
                          </div>
                        ) : messages.length === 0 ? (
                          <div className="text-center py-4 text-sm text-gray-500">
                            No messages yet
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {messages.map(message => renderMessageBubble(message))}
                            {renderTypingIndicator()}
                            <div ref={messagesEndRef} />
                          </div>
                        )}
                      </div>

                      {/* Message input */}
                      {renderMessageInputForm()}
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-500 p-4">
                      <div className="text-center">
                        <FiMessageSquare
                          size={36}
                          className="mx-auto mb-3 text-gray-400"
                        />
                        <p className="text-sm">
                          Select a user to start chatting
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Error display */}
              {renderErrorMessage()}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FloatingChat;
